import React, { useState, useRef, useEffect } from "react";
import { downloadCsv, printPdf } from "../utils/export";

/**
 * Reusable download dropdown — used by Input, Report, and Projects tabs.
 *
 * Props:
 *   getData(): { title, columns, rows }
 *     `columns`: [{ key, label }]
 *     `rows`:    [{ [key]: value, _formula_text?: string }]
 *     If `_formula_text` is set on a row, it appears in the optional
 *     "Formula" column when Include Formula is ticked.
 *
 *   filename: optional override for the downloaded file basename
 *             (defaults to the data.title from getData()).
 */
export default function DownloadMenu({ getData, filename }) {
  const [open, setOpen] = useState(false);
  const [includeFormula, setIncludeFormula] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const exportAs = (fmt) => {
    const data = (typeof getData === "function" ? getData() : null) || { columns: [], rows: [] };
    const payload = {
      title: filename || data.title || "export",
      columns: data.columns || [],
      rows: data.rows || [],
      includeFormula,
    };
    setOpen(false);
    if (fmt === "csv") downloadCsv(payload);
    else printPdf(payload);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>
      <label className="text-xxs"
        title="Adds a 'Formula' column to the export showing the computation rule / formula expression where defined."
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          cursor: "pointer", color: "var(--text-muted)",
        }}>
        <input type="checkbox" checked={includeFormula}
          onChange={(e) => setIncludeFormula(e.target.checked)}
          style={{ accentColor: "var(--accent)", width: 14, height: 14 }} />
        Include Formula
      </label>
      <button type="button" className="btn btn-secondary btn-sm"
        onClick={() => setOpen((p) => !p)}
        title="Download this view">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ marginRight: 6, verticalAlign: "-2px" }}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0,
          zIndex: 60,
          background: "var(--bg-secondary, #15192a)",
          border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)", padding: 4,
          minWidth: 160,
        }}>
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => exportAs("csv")}
            style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}>
            📊 Excel (.csv)
          </button>
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => exportAs("pdf")}
            style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}>
            📄 PDF
          </button>
        </div>
      )}
    </div>
  );
}
